import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Typography, Button, Chip, IconButton, Menu, MenuItem, Avatar, Skeleton} from '@mui/material'
import {Add, MoreVert} from '@mui/icons-material'
import {useParams} from 'react-router-dom'
import {format, parseISO} from 'date-fns'
import {useMobile} from '../../hooks'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {clientApi, caregiverApi} from '../../services'
import {parseError, getInitials, getColorFromName} from '../../utils/helpers'
import {AssignNewCaregiver, UpdateAssignedCaregiverService, RemoveAssignedCaregiverDialog} from './components'
import {SkeletonContainer, AvatarSkeleton, ChipSkeleton, TextSkeleton} from '../../components'

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const [anchorEl, setAnchorEl] = useState(null)
    const [openAssignNewCaregiver, setOpenAssignNewCaregiver] = useState(false)
    const [openUpdateAssignedCaregiverService, setOpenUpdateAssignedCaregiverService] = useState(false)
    const [openRemoveAssignedCaregiverDialog, setOpenRemoveAssignedCaregiverDialog] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null)
    const {clientId} = useParams()
    const {mobile} = useMobile()
    const [client, setClient] = useState(null)
    const [caregivers, setCaregivers] = useState([])
    const [clientCaregivers, setClientCaregivers] = useState([])
    const [filterdClientCaregivers, setFilterdClientCaregivers] = useState([])
    const [serviceFilters, setServiceFilters] = useState([])
    const [selectedServiceFilter, setSelectedServiceFilter] = useState('All')
    const [skeleton, setSkeleton] = useState(true)

    useEffect(() => {
        ;(async () => {
            if (branchId) {
                try {
                    setSkeleton(true)

                    const {
                        data: {data: data1},
                    } = await clientApi.getBranchClient(branchId, clientId)
                    setClient(data1)

                    const {data: data2} = await caregiverApi.getBranchCaregivers(branchId, {page_size: 100})
                    setCaregivers(data2?.caregivers)

                    loadData(true)
                } catch (error) {
                    setSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                }
            }
        })()
    }, [branchId])

    useEffect(() => {
        if (selectedServiceFilter == 'All') {
            setFilterdClientCaregivers(clientCaregivers)
        } else {
            setFilterdClientCaregivers(clientCaregivers?.filter(item => item?.filters?.includes(selectedServiceFilter)))
        }
    }, [selectedServiceFilter])

    // The first load shows the skeleton; refetches after assigning, updating or removing keep the global spinner.
    const loadData = (initial = false) => {
        const done = () => (initial ? setSkeleton(false) : setLoading(false))
        if (!initial) setLoading(true)
        clientApi
            .getClientCaregivers(branchId, clientId)
            .then(({data: {caregivers}}) => {
                done()
                const temp = []
                const filters = []
                if (caregivers) {
                    for (const caregiver of caregivers) {
                        temp.push({...caregiver, filters: caregiver?.services?.map(item => item?.service_name)})
                        if (caregiver?.services) {
                            for (const service of caregiver?.services) {
                                if (!filters.includes(service?.service_name)) {
                                    filters.push(service?.service_name)
                                }
                            }
                        }
                    }
                }
                setClientCaregivers(temp)
                setFilterdClientCaregivers(temp)
                setServiceFilters(['All', ...filters?.sort()])
            })
            .catch(error => {
                done()
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const assignNewCaregiver = () => {
        setOpenAssignNewCaregiver(true)
    }

    const updateAssignedCaregiverService = () => {
        setOpenUpdateAssignedCaregiverService(true)
        menuClose()
    }

    const removeAssignedCaregiver = () => {
        setOpenRemoveAssignedCaregiverDialog(true)
        menuClose()
    }

    return (
        <>
            {mobile ? (
                <>
                    <Button variant="contained" startIcon={<Add />} fullWidth onClick={assignNewCaregiver}>
                        Assign Caregiver
                    </Button>
                    <br />
                </>
            ) : (
                <>
                    <div className="header">
                        <Typography variant="h6" color="text.primary">
                            Assigned Caregivers
                        </Typography>
                        <Button variant="contained" startIcon={<Add />} onClick={assignNewCaregiver}>
                            Assign Caregiver
                        </Button>
                    </div>
                </>
            )}
            <br />
            {skeleton && (
                <SkeletonContainer>
                    <div className="content" style={{flexWrap: 'wrap'}}>
                        {[44, 120, 104].map((width, index) => (
                            <ChipSkeleton key={index} width={width} height={32} />
                        ))}
                    </div>
                    {[0, 1, 2].map(index => (
                        <div key={index}>
                            <br />
                            <div className="card">
                                <div className="top">
                                    <div className="content">
                                        <AvatarSkeleton size={40} />
                                        <div className="client-profile-card">
                                            <TextSkeleton width={index % 2 ? 120 : 150} fontSize={18} />
                                            <TextSkeleton width={index % 2 ? 160 : 190} fontSize={16} />
                                        </div>
                                    </div>
                                    <Skeleton variant="circular" width={24} height={24} style={{margin: 8}} />
                                </div>
                                <hr className="line" />
                                <div className="content">
                                    <TextSkeleton width={110} />
                                </div>
                                <div className="content">
                                    <ChipSkeleton width={120} height={32} />
                                    {index !== 1 && <ChipSkeleton width={104} height={32} />}
                                </div>
                                <div className="content">
                                    <TextSkeleton width={90} />
                                    <TextSkeleton width={80} />
                                </div>
                                <div className="content">
                                    <TextSkeleton width={260} />
                                </div>
                            </div>
                        </div>
                    ))}
                </SkeletonContainer>
            )}
            <div className="content" style={{flexWrap: 'wrap'}}>
                {serviceFilters?.map((item, index) => (
                    <Chip
                        label={item}
                        color={item == selectedServiceFilter ? 'primary' : 'action.selected'}
                        variant="filled"
                        key={index}
                        className="filter"
                        onClick={e => setSelectedServiceFilter(item)}
                    />
                ))}
            </div>
            {filterdClientCaregivers?.map((item, index) => (
                <div key={index}>
                    <br />
                    <div className="card">
                        <div className="top">
                            <div className="content">
                                {item?.caregiver?.avatar_url ? (
                                    <Avatar src={item?.caregiver?.avatar_url} style={{width: 40, height: 40}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.caregiver?.first_name + ' ' + item?.caregiver?.last_name),
                                            width: 40,
                                            height: 40,
                                            fontSize: 20,
                                        }}>
                                        {getInitials(item?.caregiver?.first_name + ' ' + item?.caregiver?.last_name)}
                                    </Avatar>
                                )}
                                <div className="client-profile-card">
                                    <Typography variant="h7" color="text.primary" className="name">
                                        {item?.caregiver?.first_name + ' ' + item?.caregiver?.last_name}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" className="name">
                                        {item?.caregiver?.email}
                                    </Typography>
                                </div>
                            </div>
                            <IconButton onClick={e => menuOpen(e, item)}>
                                <MoreVert />
                            </IconButton>
                        </div>
                        <hr className="line" />
                        <div className="content">
                            <Typography variant="h9" color="text.secondary">
                                Assigned Service:
                            </Typography>
                        </div>
                        <div className="content">
                            {item?.services?.map((item2, index2) => (
                                <Chip label={item2?.service_name} color="action.selected" variant="filled" key={index2} />
                            ))}
                        </div>
                        <div className="content">
                            <Typography variant="h9" color="text.secondary">
                                Last Schedule:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {item?.schedule_stats?.last_schedule_date
                                    ? format(parseISO(item.schedule_stats.last_schedule_date), 'MM/dd/yyyy')
                                    : '-'}
                            </Typography>
                        </div>
                        <div className="content">
                            <Typography variant="h9" color="text.secondary">
                                Completed Schedule:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {item?.schedule_stats?.completed_schedules ?? 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" style={{marginLeft: 12, marginRight: 12}}>
                                |
                            </Typography>
                            <Typography variant="h9" color="text.secondary">
                                Active Schedule:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {item?.schedule_stats?.active_schedules ?? 0}
                            </Typography>
                        </div>
                    </div>
                </div>
            ))}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <div>
                    <MenuItem onClick={updateAssignedCaregiverService}>Update Details</MenuItem>
                    <MenuItem onClick={removeAssignedCaregiver} sx={theme => ({color: theme.palette.error.main})}>
                        Remove Assigned Caregiver
                    </MenuItem>
                </div>
            </Menu>
            <AssignNewCaregiver
                open={openAssignNewCaregiver}
                setOpen={setOpenAssignNewCaregiver}
                loadData={loadData}
                client={client}
                allCaregivers={caregivers}
                clientCaregivers={clientCaregivers}
            />
            <UpdateAssignedCaregiverService
                open={openUpdateAssignedCaregiverService}
                setOpen={setOpenUpdateAssignedCaregiverService}
                data={selectedRow}
                loadData={loadData}
                client={client}
                caregivers={caregivers}
            />
            <RemoveAssignedCaregiverDialog
                open={openRemoveAssignedCaregiverDialog}
                setOpen={setOpenRemoveAssignedCaregiverDialog}
                data={selectedRow}
                loadData={loadData}
            />
        </>
    )
}
